import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import { fetchDefaultGrouping } from '../utils/storeSettings';

interface ApiSalesSummaryRow {
  productId?: number | null;
  productName: string;
  prevDayQty: number;
  prevDaySum: number;
  dayQty: number;
  daySum: number;
  prevWeekQty: number;
  prevWeekSum: number;
  weekQty: number;
  weekSum: number;
  monthQty: number;
  monthSum: number;
  prevMonthQty: number;
  prevMonthSum: number;
}

interface SalesSummaryRow extends ApiSalesSummaryRow {
  rowKey: string;
}

type SortableKey = keyof Pick<
  ApiSalesSummaryRow,
  | 'productName'
  | 'dayQty'
  | 'daySum'
  | 'weekQty'
  | 'weekSum'
  | 'monthQty'
  | 'monthSum'
  | 'prevMonthQty'
  | 'prevMonthSum'
>;

type ColumnAlignment = 'left' | 'right';

interface ColumnDefinition {
  key: SortableKey;
  label: string;
  align?: ColumnAlignment;
  isCurrency?: boolean;
  comparisonKey?: keyof Pick<
    SalesSummaryRow,
    'prevDayQty' | 'prevDaySum' | 'prevWeekQty' | 'prevWeekSum' | 'prevMonthQty' | 'prevMonthSum'
  >;
  minWidthClass?: string;
}

interface ColumnGroup {
  label: string;
  columns: ColumnDefinition[];
  rowSpan?: number;
}

const buildColumnGroups = (viewMode: SalesViewMode): ColumnGroup[] => [
  {
    label: viewMode === 'groups' ? 'Категория' : 'Товар',
    rowSpan: 2,
    columns: [
      {
        key: 'productName',
        label: viewMode === 'groups' ? 'Категория' : 'Товар',
        align: 'left',
        minWidthClass: 'w-[10%] max-w-[10%]',
      },
    ],
  },
  {
    label: 'День',
    columns: [
      {
        key: 'dayQty',
        label: 'шт',
        align: 'right',
        comparisonKey: 'prevDayQty',
        minWidthClass: 'min-w-[140px]',
      },
      {
        key: 'daySum',
        label: '₽',
        align: 'right',
        isCurrency: true,
        comparisonKey: 'prevDaySum',
        minWidthClass: 'min-w-[160px]',
      },
    ],
  },
  {
    label: 'Неделя',
    columns: [
      {
        key: 'weekQty',
        label: 'шт',
        align: 'right',
        comparisonKey: 'prevWeekQty',
        minWidthClass: 'min-w-[140px]',
      },
      {
        key: 'weekSum',
        label: '₽',
        align: 'right',
        isCurrency: true,
        comparisonKey: 'prevWeekSum',
        minWidthClass: 'min-w-[160px]',
      },
    ],
  },
  {
    label: 'Месяц',
    columns: [
      {
        key: 'monthQty',
        label: 'шт',
        align: 'right',
        comparisonKey: 'prevMonthQty',
        minWidthClass: 'min-w-[140px]',
      },
      {
        key: 'monthSum',
        label: '₽',
        align: 'right',
        isCurrency: true,
        comparisonKey: 'prevMonthSum',
        minWidthClass: 'min-w-[160px]',
      },
    ],
  },
  {
    label: 'Прошлый месяц',
    columns: [
      {
        key: 'prevMonthQty',
        label: 'шт',
        align: 'right',
        minWidthClass: 'min-w-[140px]',
      },
      {
        key: 'prevMonthSum',
        label: '₽',
        align: 'right',
        isCurrency: true,
        minWidthClass: 'min-w-[160px]',
      },
    ],
  },
];

function formatNumeric(value: number, digits?: number): string {
  const hasFraction = Math.abs(value % 1) > 0;
  const fractionDigits = digits ?? (hasFraction ? 2 : 0);
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatInteger(value: number): string {
  return formatNumeric(value, 0);
}

function formatCurrency(value: number): string {
  return `${formatNumeric(value)} ₽`;
}

interface DeltaDisplay {
  arrow: string;
  colorClass: string;
  text: string;
}

function getDeltaDisplay(current: number, previous?: number | null): DeltaDisplay | null {
  if (previous === undefined || previous === null) {
    return null;
  }

  if (previous === 0) {
    if (current === 0) {
      return { arrow: '→', colorClass: 'text-gray-500', text: '0%' };
    }
    const arrow = current > 0 ? '▲' : current < 0 ? '▼' : '→';
    const colorClass = current > 0 ? 'text-green-600' : current < 0 ? 'text-red-600' : 'text-gray-500';
    return { arrow, colorClass, text: '—' };
  }

  const diff = ((current - previous) / Math.abs(previous)) * 100;
  if (!Number.isFinite(diff)) {
    return null;
  }

  const threshold = 0.05;
  const arrow = diff > threshold ? '▲' : diff < -threshold ? '▼' : '→';
  const colorClass = diff > threshold ? 'text-green-600' : diff < -threshold ? 'text-red-600' : 'text-gray-500';
  const absDiff = Math.abs(diff);
  const digits = absDiff % 1 === 0 ? 0 : 2;
  const text = `${formatNumeric(absDiff, digits)}%`;
  return { arrow, colorClass, text };
}

type SalesViewMode = 'products' | 'groups';

const SalesSummaryTable: React.FC = () => {
  const [data, setData] = useState<SalesSummaryRow[]>([]);
  const [sortKey, setSortKey] = useState<SortableKey>('monthSum');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<SalesViewMode>('groups');
  const userChangedView = useRef(false);

  const handleViewModeChange = (mode: SalesViewMode) => {
    userChangedView.current = true;
    setViewMode(mode);
  };

  useEffect(() => {
    let isActive = true;
    fetchDefaultGrouping().then(mode => {
      if (!isActive || !mode || userChangedView.current) return;
      setViewMode(mode);
    });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    async function load() {
      const query = new URLSearchParams();
      if (viewMode === 'groups') {
        query.set('view', 'groups');
      }
      const res = await apiFetch(`/api/dashboard/sales-summary${query.toString() ? `?${query}` : ''}`);
      if (!res.ok) {
        throw new Error('Не удалось получить данные по продажам');
      }
      const rows: ApiSalesSummaryRow[] = await res.json();
      const mapped = rows.map(row => ({
        ...row,
        rowKey:
          row.productId != null
            ? `product-${row.productId}`
            : `group-${row.productName || 'Без категории'}`,
      }));
      setData(mapped);
    }
    load().catch(console.error);
  }, [viewMode]);

  const columnGroups = useMemo(() => buildColumnGroups(viewMode), [viewMode]);
  const columns = useMemo(() => columnGroups.flatMap(group => group.columns), [columnGroups]);

  const sortedData = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        const compare = av.localeCompare(bv, 'ru');
        return sortDirection === 'asc' ? compare : -compare;
      }
      const aNum = Number(av);
      const bNum = Number(bv);
      if (aNum === bNum) return 0;
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return copy;
  }, [data, sortDirection, sortKey]);

  const totals = useMemo(
    () =>
      data.reduce(
        (acc, row) => {
          acc.prevDayQty += row.prevDayQty || 0;
          acc.prevDaySum += row.prevDaySum || 0;
          acc.dayQty += row.dayQty || 0;
          acc.daySum += row.daySum || 0;
          acc.prevWeekQty += row.prevWeekQty || 0;
          acc.prevWeekSum += row.prevWeekSum || 0;
          acc.weekQty += row.weekQty || 0;
          acc.weekSum += row.weekSum || 0;
          acc.monthQty += row.monthQty || 0;
          acc.monthSum += row.monthSum || 0;
          acc.prevMonthQty += row.prevMonthQty || 0;
          acc.prevMonthSum += row.prevMonthSum || 0;
          return acc;
        },
        {
          prevDayQty: 0,
          prevDaySum: 0,
          dayQty: 0,
          daySum: 0,
          prevWeekQty: 0,
          prevWeekSum: 0,
          weekQty: 0,
          weekSum: 0,
          monthQty: 0,
          monthSum: 0,
          prevMonthQty: 0,
          prevMonthSum: 0,
        }
      ),
    [data]
  );

  const handleSort = (key: SortableKey) => {
    if (sortKey === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'productName' ? 'asc' : 'desc');
    }
  };

  const getSortIndicator = (key: SortableKey) => {
    if (sortKey !== key) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const renderNumericContent = (value: number, col: ColumnDefinition, previous?: number | null) => {
    const formattedValue = col.isCurrency ? formatCurrency(value) : formatInteger(value);
    if (!col.comparisonKey) {
      return formattedValue;
    }
    const delta = getDeltaDisplay(value, previous);
    const alignment = col.align ?? 'left';
    const previousFormatted =
      previous !== undefined && previous !== null
        ? col.isCurrency
          ? formatCurrency(previous)
          : formatInteger(previous)
        : null;
    return (
      <div
        className={`flex ${alignment === 'right' ? 'justify-end' : 'justify-start'} items-center gap-2`}
      >
        <span>{formattedValue}</span>
        {delta && (
          <span className={`flex items-center gap-1 text-xs font-medium ${delta.colorClass}`}>
            <span className="relative flex items-center group">
              <span className="cursor-help">{delta.arrow}</span>
              {previousFormatted && (
                <span className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 -translate-y-1 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                  Прошлый период: {previousFormatted}
                </span>
              )}
            </span>
            <span>{delta.text}</span>
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow mt-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between bg-white px-4 py-2 border-b rounded-t-2xl">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            Продажи по {viewMode === 'groups' ? 'категориям' : 'товарам'}
          </h2>
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  viewMode === 'products' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleViewModeChange('products')}
              >
                по товарам
              </button>
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  viewMode === 'groups' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleViewModeChange('groups')}
              >
                по категориям
              </button>
          </div>
        </div>
      </div>
      <div className="overflow-auto max-h-[70vh] rounded-xl border border-gray-200">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              <th className="w-12 min-w-[3rem] sm:w-16 py-2 px-2 text-center font-bold sticky left-0 z-20 bg-gray-50" rowSpan={2}>
                №
              </th>
              {columnGroups.map(group => {
                const isSingleColumn = group.columns.length === 1;
                const rowSpan = group.rowSpan ?? 1;
                const colSpan = rowSpan === 2 ? 1 : group.columns.length;
                const sortable = rowSpan === 2 && isSingleColumn;
                const groupColumn = group.columns[0];
                const alignmentClass = sortable
                  ? groupColumn?.align === 'left'
                    ? 'text-left'
                    : 'text-right'
                  : 'text-center';
                const widthClass = sortable && groupColumn?.minWidthClass ? groupColumn.minWidthClass : '';
                const isNameColumn = groupColumn?.key === 'productName';
                const stickyClass = isNameColumn ? 'sticky left-12 sm:left-16 z-20 bg-gray-50' : '';

                return (
                  <th
                    key={group.label}
                    rowSpan={rowSpan}
                    colSpan={colSpan}
                    className={`py-2 px-2 font-bold ${alignmentClass} ${widthClass} ${stickyClass} ${
                      sortable ? 'cursor-pointer select-none' : ''
                    }`}
                    onClick={sortable && groupColumn ? () => handleSort(groupColumn.key) : undefined}
                  >
                    <span>{group.label}</span>
                    {sortable && groupColumn && (
                      <span className="ml-1 text-gray-500">{getSortIndicator(groupColumn.key)}</span>
                    )}
                  </th>
                );
              })}
            </tr>
            <tr>
              {columnGroups.flatMap(group =>
                group.rowSpan === 2
                  ? []
                  : group.columns.map(col => {
                      const alignmentClass = col.align === 'left' ? 'text-left' : 'text-right';
                      return (
                        <th
                          key={`${group.label}-${col.key}`}
                          className={`py-2 px-2 ${alignmentClass} ${col.minWidthClass ?? ''} font-bold cursor-pointer select-none`}
                          onClick={() => handleSort(col.key)}
                        >
                          <span className="mr-1">{col.label}</span>
                          <span className="text-gray-500">{getSortIndicator(col.key)}</span>
                        </th>
                      );
                    })
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((row, idx) => (
              <tr key={row.rowKey} className="hover:bg-gray-50">
                <td className="py-2 text-center sticky left-0 z-10 bg-white w-12 min-w-[3rem] sm:w-16">{idx + 1}</td>
                {columns.map(col => {
                  const alignmentClass = col.align === 'left' ? 'text-left' : 'text-right';
                  const widthClass = col.minWidthClass ?? '';
                  const whitespaceClass = 'whitespace-nowrap';
                  const overflowClass = col.key === 'productName' ? 'max-w-0' : '';
                  const stickyClass = col.key === 'productName' ? 'sticky left-12 sm:left-16 z-10 bg-white' : '';
                  const value = row[col.key];
                  const previousValue = col.comparisonKey ? row[col.comparisonKey] : undefined;
                  const previousNumeric = typeof previousValue === 'number' ? previousValue : null;
                  let cellContent: React.ReactNode;

                  if (typeof value === 'number') {
                    cellContent = renderNumericContent(value, col, previousNumeric);
                  } else if (col.key === 'productName' && typeof value === 'string') {
                    cellContent = <div className="truncate w-full">{value}</div>;
                  } else {
                    cellContent = value ?? '—';
                  }

                  const cellTitle = col.key === 'productName' && typeof value === 'string' ? value : undefined;

                  return (
                    <td
                      key={col.key}
                      className={`py-2 px-2 ${alignmentClass} ${whitespaceClass} ${widthClass} ${overflowClass} ${stickyClass}`}
                      title={cellTitle}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))}
            {sortedData.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="py-4 text-center text-gray-500">
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-gray-50 border-t">
            <tr>
              <td className="py-2 text-center font-bold sticky left-0 z-10 bg-gray-50">Итого</td>
              {columns.map(col => {
                const alignmentClass = col.align === 'left' ? 'text-left' : 'text-right';
                const widthClass = col.minWidthClass ?? '';
                const whitespaceClass = 'whitespace-nowrap';
                const overflowClass = col.key === 'productName' ? 'max-w-0' : '';
                const stickyClass = col.key === 'productName' ? 'sticky left-12 sm:left-16 z-10 bg-gray-50' : '';
                if (col.key === 'productName') {
                  return (
                    <td
                      key={col.key}
                      className={`py-2 px-2 ${alignmentClass} ${whitespaceClass} ${widthClass} ${overflowClass} ${stickyClass} font-bold`}
                    ></td>
                  );
                }
                const value = totals[col.key as Exclude<SortableKey, 'productName'>];
                const previousValue = col.comparisonKey ? totals[col.comparisonKey] : undefined;
                const previousNumeric = typeof previousValue === 'number' ? previousValue : null;
                return (
                  <td
                    key={col.key}
                    className={`py-2 px-2 ${alignmentClass} ${whitespaceClass} ${widthClass} ${overflowClass} ${stickyClass} font-bold`}
                  >
                    {renderNumericContent(value, col, previousNumeric)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SalesSummaryTable;
